import { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
//@ts-ignore
import { db } from "../../utils/firebase";

type Staff = {
  name: string;
  id: string;
  active: boolean;
};

interface Props {
  onClose: () => void;
  userId: string;
}

export default function StaffDrawer({ onClose, userId }: Props) {
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ---------------------------
  // Fetch Existing Staff
  // ---------------------------
  useEffect(() => {
    const fetchData = async () => {
      const snap = await getDoc(doc(db, "userDetails", userId));

      if (snap.exists()) {
        const data = snap.data();
        setStaffList(data?.businessRules?.staff || []);
      }
    };

    fetchData();
  }, [userId]);

  // ---------------------------
  // Handlers
  // ---------------------------
  const addStaff = () => {
    setStaffList([
      ...staffList,
      { name: "", id: "", active: true },
    ]);
  };

  const removeStaff = (index: number) => {
    const updated = [...staffList];
    updated.splice(index, 1);
    setStaffList(updated);
  };

  const updateField = (
    index: number,
    field: keyof Staff,
    value: string | boolean
  ) => {
    const updated = [...staffList];
    //@ts-ignore
    updated[index][field] = value;
    setStaffList(updated);
  };

  const toggleActive = (index: number) => {
    const updated = [...staffList];
    updated[index].active = !updated[index].active;
    setStaffList(updated);
  };

  // ---------------------------
  // Validation
  // ---------------------------
  const isValid = () => {
    for (let staff of staffList) {
      if (!staff.name || !staff.id) return false;
    }
    return true;
  };

  // ---------------------------
  // Save
  // ---------------------------
  const handleSave = async () => {
    if (!isValid()) {
      alert("Please fill all staff details");
      return;
    }

    try {
      setLoading(true);

      await updateDoc(doc(db, "userDetails", userId), {
        "businessRules.staff": staffList,
      });

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 2000);

    } catch (error) {
      console.error(error);
      alert("Error saving staff");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // UI
  // ---------------------------
  return (
    <div className="fixed inset-0 z-50 flex">
      
      {/* Overlay */}
      <div
        className="flex-1 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="w-[480px] bg-white h-full shadow-2xl flex flex-col animate-slideIn">

        {/* Header */}
        <div className="px-6 py-5 border-b bg-gradient-to-r from-[#f4f9ed] to-white">
          <h2 className="text-2xl font-semibold text-gray-900">
            Staff Management
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Add and manage your staff members
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">

          {staffList.map((staff, index) => (
            <div
              key={index}
              className="rounded-2xl border border-gray-200 p-5 bg-white shadow-sm hover:shadow-md transition"
            >
              {/* Header Row */}
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold text-gray-800">
                  Staff #{index + 1}
                </span>

                {/* Toggle */}
                <button
                  onClick={() => toggleActive(index)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                    staff.active ? "bg-[#6cbb23]" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      staff.active ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Inputs */}
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Staff Name"
                  value={staff.name}
                  onChange={(e) =>
                    updateField(index, "name", e.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#6cbb23] focus:border-[#6cbb23]"
                />

                <input
                  type="text"
                  placeholder="Staff ID"
                  value={staff.id}
                  onChange={(e) =>
                    updateField(index, "id", e.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#6cbb23] focus:border-[#6cbb23]"
                />
              </div>

              {/* Remove */}
              <button
                onClick={() => removeStaff(index)}
                className="mt-4 text-sm text-red-500 hover:underline"
              >
                Remove Staff
              </button>
            </div>
          ))}

          {/* Add Button */}
          <button
            onClick={addStaff}
            className="w-full border-2 border-dashed border-[#6cbb23] text-[#6cbb23] py-3 rounded-2xl font-medium hover:bg-[#f4f9ed] transition"
          >
            + Add Staff Member
          </button>
        </div>

        {/* Save */}
        <div className="px-6 py-4 border-t bg-white">
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-[#6cbb23] text-white py-3 rounded-2xl font-semibold hover:opacity-90 transition active:scale-[0.98]"
          >
            {loading ? "Saving..." : "Save Staff"}
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60]">
          <div className="bg-[#6cbb23] text-white px-6 py-3 rounded-2xl shadow-xl flex items-center gap-2">
            ✓ Staff saved successfully
          </div>
        </div>
      )}
    </div>
  );
}